import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Space } from 'antd';
import { markHomeStep } from '../../services/SettingsService';
import { appItem, openApp, runItems, runSummary, startProject, stopProject, withDefaultRunnable } from './projectRun';
import useDefaultApp from '../../hooks/useDefaultApp';
import StartButton, { StartAnnouncer } from './StartButton';
import { allRunning } from './startProgress';
import { clearStart, projectStartKey, runStart, stopStart, useStart } from './startRuns';

/**
 * Start / Stop and Open app for one project on the Projects list (its `GET /api/v1/projects/:id/services` answer in
 * `state`). Asks for the state once it scrolls into view (`onVisible`, the list loads rows lazily).
 * - Start when nothing runs (every service of the project, never started ones too), Stop when anything runs. A start
 *   follows every service until all of them run: "Starting 1/3…" with the list of services and their step, then the
 *   usual Stop; when some did not start, "2/3 running · 1 failed" with Retry.
 * - Open app: the running app (the primary service) in a new tab, as the header's Open App does.
 * - A viewer (`canRun` false) gets Open app only, never Start / Stop.
 * - `startType`: Start's antd button type, `primary` by default (a card's foot); a list row passes `default`, so a
 *   column of rows stays light (Retry, after a start that partly failed, too). Stop and Open app are always default
 *   buttons of the same `size`.
 * Clicks never reach the row (which opens the project).
 */
export default function ProjectRunControls({
  projectId, state = undefined, onVisible, onHidden, onChanged, size = 'small', startType = 'primary', pollMs = undefined,
}) {
  const ref = useRef(null);
  const key = projectStartKey(projectId);
  const { progress: last, starting } = useStart(key);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState(null);
  const busy = starting ? 'start' : (stopping ? 'stop' : null);

  useEffect(() => {
    const node = ref.current;
    if (!node || !onVisible) { return undefined; }
    if (typeof IntersectionObserver === 'undefined') {
      onVisible(projectId);
      return () => onHidden?.(projectId);
    }
    // The row, not this cell: a row on screen loads its state even when a narrow window leaves this cell off to the
    // right (it would otherwise stay "Stopped" forever); a margin loads the rows just below as well.
    const target = node.closest('.list-row, .project-card, [role="row"]') || node;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? onVisible(projectId) : onHidden?.(projectId)));
    }, { rootMargin: '200px 0px' });
    observer.observe(target);
    return () => {
      observer.disconnect();
      onHidden?.(projectId);
    };
  }, [projectId, onVisible, onHidden]);

  const items = runItems(state);
  const summary = runSummary(state);
  // What did not start, until it runs after all (started from elsewhere, or by the agent).
  const progress = !starting && last && !allRunning(last) && summary.total > 0 && summary.running === summary.total ? null : last;

  // Every service of the project (Retry: the same; those that run are counted as running).
  const start = () => {
    setError(null);
    runStart(key, (onProgress, shouldStop) => startProject(projectId, items, { pollMs, onEach: () => onChanged?.(projectId), onProgress, shouldStop }))
      .then(() => { markHomeStep('previewOpened'); })
      .finally(() => onChanged?.(projectId));
  };
  const stop = async() => {
    setStopping(true);
    setError(null);
    // A start in progress starts no more services; the ones it is building are cancelled by their Stop.
    stopStart(key);
    clearStart(key);
    try {
      const { error: failed } = await stopProject(projectId, items, { onEach: () => onChanged?.(projectId) });
      if (failed) { setError(failed); }
    } finally {
      setStopping(false);
      onChanged?.(projectId);
    }
  };

  const stopClick = (event) => { event.stopPropagation(); };
  // The project's default app is read only for a row that has a running app to open.
  const anyApp = appItem(projectId, items);
  const { defaultApp, defaultRunnable } = useDefaultApp(projectId, { enabled: Boolean(anyApp) });
  // The default app may be a file pinned in a service (its own session, utils/defaultApp).
  const app = anyApp ? appItem(projectId, withDefaultRunnable(items, defaultRunnable, state?.services), defaultApp) : null;
  const loaded = Boolean(state);
  const showProgress = starting || (Boolean(progress) && !allRunning(progress));
  const startError = !starting && progress?.done ? progress.error : null;

  return (
    <span className="projects-run" onClick={stopClick} onKeyDown={stopClick} ref={ref} role="presentation">
      <StartAnnouncer progress={progress} />
      {loaded && (
        <Space size={4} wrap>
          {state.canRun !== false && items.length > 0 && (
            showProgress || summary.running === 0 ? (
              <>
                <StartButton
                  announce={false}
                  ariaLabel={`Start ${projectId}`}
                  disabled={Boolean(busy)}
                  label="Start"
                  onDismiss={busy ? undefined : () => clearStart(key)}
                  onRetry={busy ? undefined : start}
                  onStart={start}
                  progress={progress}
                  retryType={startType}
                  size={size}
                  type={startType}
                />
                {/* While it starts or builds: Stop ends the builds too (their installs are killed). */}
                {starting && (
                  <Button aria-label={`Stop ${projectId}`} disabled={stopping} loading={stopping} onClick={stop} size={size}>Stop</Button>
                )}
              </>
            ) : (
              <Button aria-label={`Stop ${projectId}`} disabled={Boolean(busy)} loading={busy === 'stop'} onClick={stop} size={size}>Stop</Button>
            )
          )}
          {app && !(progress && !progress.done) && (
            <Button aria-label={`Open app of ${projectId}`} onClick={() => openApp(projectId, app)} size={size} title="Open the running app in a new tab">
              Open app
            </Button>
          )}
          {(error || startError) && <span className="sbx-action-error" role="alert" title={error || startError}>{error || startError}</span>}
        </Space>
      )}
    </span>
  );
}

ProjectRunControls.propTypes = {
  projectId: PropTypes.string.isRequired,
  state: PropTypes.object,
  onVisible: PropTypes.func,
  onHidden: PropTypes.func,
  onChanged: PropTypes.func,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  /** Start's antd button type: `primary` (a card) or `default` (a list row). */
  startType: PropTypes.oneOf(['primary', 'default', 'text', 'link']),
  /** How often a start looks at the services (tests). */
  pollMs: PropTypes.number,
};
